// Performance Monitoring and Optimization Service
// Real-time performance tracking, lazy loading, and resource optimization

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface PerformanceMetrics {
  fcp: number; // First Contentful Paint
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  ttfb: number; // Time to First Byte
  inputDelay: number;
  longTasks: number;
  memoryUsage: number;
  fps: number;
}

interface ResourceTiming {
  name: string;
  type: string;
  size: number;
  duration: number;
  cached: boolean;
}

interface ErrorLog {
  id: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  component?: string;
  timestamp: Date;
  userId?: string;
  metadata?: Record<string, any>;
}

class PerformanceService {
  private metrics: PerformanceMetrics = {
    fcp: 0,
    lcp: 0,
    fid: 0,
    cls: 0,
    ttfb: 0,
    inputDelay: 0,
    longTasks: 0,
    memoryUsage: 0,
    fps: 60,
  };

  private errorLogs: ErrorLog[] = [];
  private observers: Map<string, PerformanceObserver> = new Map();
  private lastMemoryCheck = Date.now();
  private frameCount = 0;
  private lastFrameTime = performance.now();
  private fpsInterval: NodeJS.Timeout | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initPerformanceMonitoring();
    }
  }

  private initPerformanceMonitoring(): void {
    // Monitor Core Web Vitals
    this.observeCLS();
    this.observeFID();
    this.observeLCP();
    this.observeFCP();

    // Monitor memory usage
    this.startMemoryMonitoring();

    // Monitor FPS
    this.startFPSMonitoring();

    // Monitor long tasks
    this.observeLongTasks();

    // Listen for errors
    window.addEventListener('error', this.handleGlobalError.bind(this));
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
  }

  private observeCLS(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            this.metrics.cls += (entry as any).value;
          }
        }
      });
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.set('cls', observer);
    }
  }

  private observeFID(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.metrics.fid = entry.processingStart - entry.startTime;
        }
      });
      observer.observe({ type: 'first-input', buffered: true });
      this.observers.set('fid', observer);
    }
  }

  private observeLCP(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.lcp = lastEntry.startTime;
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.set('lcp', observer);
    }
  }

  private observeFCP(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          this.metrics.fcp = entries[0].startTime;
        }
      });
      observer.observe({ type: 'paint', buffered: true });
      this.observers.set('fcp', observer);
    }
  }

  private observeLongTasks(): void {
    if ('PerformanceObserver' in window && 'PerformanceLongTaskTiming' in window) {
      const observer = new PerformanceObserver((list) => {
        this.metrics.longTasks += list.getEntries().length;
      });
      observer.observe({ type: 'longtask', buffered: true });
      this.observers.set('longtasks', observer);
    }
  }

  private startMemoryMonitoring(): void {
    setInterval(() => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.metrics.memoryUsage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
      }
    }, 5000);
  }

  private startFPSMonitoring(): void {
    const measureFPS = () => {
      const now = performance.now();
      this.frameCount++;
      
      if (now >= this.lastFrameTime + 1000) {
        this.metrics.fps = Math.round((this.frameCount * 1000) / (now - this.lastFrameTime));
        this.frameCount = 0;
        this.lastFrameTime = now;
      }

      requestAnimationFrame(measureFPS);
    };

    requestAnimationFrame(measureFPS);
  }

  private handleGlobalError(event: ErrorEvent): void {
    this.logError({
      type: 'error',
      message: event.message,
      stack: event.error?.stack,
    });
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    this.logError({
      type: 'error',
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
    });
  }

  logError(log: Omit<ErrorLog, 'id' | 'timestamp'>): void {
    const errorLog: ErrorLog = {
      ...log,
      id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    this.errorLogs.push(errorLog);

    // Keep only last 100 errors
    if (this.errorLogs.length > 100) {
      this.errorLogs = this.errorLogs.slice(-100);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error logged:', errorLog);
    }
  }

  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  getErrorLogs(): ErrorLog[] {
    return [...this.errorLogs];
  }

  getResourceTiming(): ResourceTiming[] {
    const resources: ResourceTiming[] = [];
    
    if (typeof performance !== 'undefined' && 'getEntriesByType' in performance) {
      const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      
      entries.forEach(entry => {
        resources.push({
          name: entry.name,
          type: entry.initiatorType,
          size: entry.transferSize || 0,
          duration: entry.responseEnd - entry.startTime,
          cached: entry.transferSize === 0,
        });
      });
    }

    return resources;
  }

  getPerformanceScore(): number {
    const { fcp, lcp, fid, cls, fps } = this.metrics;
    
    // Calculate score based on Core Web Vitals thresholds
    const fcpScore = fcp < 1800 ? 100 : Math.max(0, 100 - (fcp - 1800) / 40);
    const lcpScore = lcp < 2500 ? 100 : Math.max(0, 100 - (lcp - 2500) / 50);
    const fidScore = fid < 100 ? 100 : Math.max(0, 100 - fid);
    const clsScore = cls < 0.1 ? 100 : Math.max(0, 100 - cls * 500);
    const fpsScore = fps >= 55 ? 100 : fps * 100 / 55;

    return Math.round((fcpScore * 0.15 + lcpScore * 0.25 + fidScore * 0.25 + clsScore * 0.25 + fpsScore * 0.1));
  }

  // Memory optimization: clear unused caches
  optimizeMemory(): void {
    // Clear old error logs
    const oneHourAgo = new Date(Date.now() - 3600000);
    this.errorLogs = this.errorLogs.filter(log => log.timestamp > oneHourAgo);
  }

  // Code splitting helper
  async loadComponentDynamically<T>(componentPath: string): Promise<T> {
    return import(/* @vite-ignore */ componentPath);
  }

  // Debounce utility
  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    
    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  // Throttle utility
  throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle = false;
    
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Virtual list helper for large datasets
  createVirtualList<T>(
    items: T[],
    itemHeight: number,
    containerHeight: number,
    overscan: number = 3
  ): { startIndex: number; endIndex: number; offsetY: number } {
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    const startIndex = Math.max(0, Math.floor(window.scrollY / itemHeight) - overscan);
    const endIndex = Math.min(items.length, startIndex + visibleCount + overscan * 2);
    const offsetY = startIndex * itemHeight;

    return { startIndex, endIndex, offsetY };
  }

  // Preload resources
  preloadResources(urls: string[], type: 'image' | 'script' | 'style' = 'image'): void {
    urls.forEach(url => {
      const link = document.createElement('preload') as HTMLLinkElement;
      link.href = url;
      
      if (type === 'image') {
        link.as = 'image';
      } else if (type === 'script') {
        link.as = 'script';
        link.rel = 'preload';
      } else if (type === 'style') {
        link.as = 'style';
      }

      document.head.appendChild(link);
    });
  }

  // Lazy load images
  lazyLoadImage(img: HTMLImageElement, src: string): void {
    img.dataset.src = src;
    img.classList.add('lazy-loading');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          img.src = img.dataset.src || '';
          img.classList.remove('lazy-loading');
          img.classList.add('lazy-loaded');
          observer.unobserve(img);
        }
      });
    });

    observer.observe(img);
  }

  // Service worker registration for offline support
  async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', registration);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  // Cache API for offline data
  async cacheData(key: string, data: any, ttl: number = 3600000): Promise<void> {
    const cacheData = {
      data,
      expiry: Date.now() + ttl,
    };
    localStorage.setItem(`cache_${key}`, JSON.stringify(cacheData));
  }

  async getCachedData<T>(key: string): Promise<T | null> {
    const cached = localStorage.getItem(`cache_${key}`);
    if (!cached) return null;

    try {
      const { data, expiry } = JSON.parse(cached);
      if (Date.now() > expiry) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }
      return data as T;
    } catch {
      return null;
    }
  }

  clearCache(): void {
    Object.keys(localStorage)
      .filter(key => key.startsWith('cache_'))
      .forEach(key => localStorage.removeItem(key));
  }

  // Cleanup
  destroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    
    if (this.fpsInterval) {
      clearInterval(this.fpsInterval);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this.handleGlobalError.bind(this));
      window.removeEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    }

    this.errorLogs = [];
    this.metrics = { fcp: 0, lcp: 0, fid: 0, cls: 0, ttfb: 0, inputDelay: 0, longTasks: 0, memoryUsage: 0, fps: 60 };
  }
}

export const performanceService = new PerformanceService();

interface PerformanceContextType {
  metrics: PerformanceMetrics;
  score: number;
  errors: ErrorLog[];
  isHealthy: boolean;
  getResourceTiming: () => ResourceTiming[];
  clearErrors: () => void;
}

const PerformanceContext = createContext<PerformanceContextType | null>(null);

export function PerformanceProvider({ children }: { children: React.ReactNode }) {
  const [metrics, setMetrics] = useState<PerformanceMetrics>(performanceService.getMetrics());
  const [score, setScore] = useState(100);
  const [errors, setErrors] = useState<ErrorLog[]>([]);

  useEffect(() => {
    // Update metrics every second
    const interval = setInterval(() => {
      setMetrics(performanceService.getMetrics());
      setScore(performanceService.getPerformanceScore());
      setErrors(performanceService.getErrorLogs());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getResourceTiming = useCallback(() => {
    return performanceService.getResourceTiming();
  }, []);

  const clearErrors = useCallback(() => {
    // Not exposed - errors are cleared after 1 hour automatically
  }, []);

  const isHealthy = score >= 70 && metrics.fid < 100 && metrics.cls < 0.1;

  return (
    <PerformanceContext.Provider value={{ metrics, score, errors, isHealthy, getResourceTiming, clearErrors }}>
      {children}
    </PerformanceContext.Provider>
  );
}

export function usePerformance() {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
}

// Performance-aware component wrapper
export function withPerformance<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) {
  return function PerformanceWrapper(props: P) {
    useEffect(() => {
      performanceService.getMetrics(); // Track component render
      return () => {
        performanceService.logError({
          type: 'info',
          message: `${componentName} unmounted`,
        });
      };
    }, []);

    return <WrappedComponent {...props} />;
  };
}

export default performanceService;